import { useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { InactivityWarningDialog } from '@nesso/ui/blocks/inactivity-warning-dialog';
import { useInactivityTimer } from '@nesso/ui/providers/inactivity-timer-provider';

import { adminUserOptions } from '@/entities/admin-user';
import { useSignOut } from '@/features/identity/sign-out';

const INACTIVITY_WARNING_ACTION_ID = 'inactivity-warning';
const INACTIVITY_SIGN_OUT_ACTION_ID = 'inactivity-sign-out';
const INACTIVITY_WARNING_TIMEOUT = 10 * 60 * 1000;
const INACTIVITY_SIGN_OUT_TIMEOUT = 15 * 60 * 1000;

export function InactivitySignOutProvider() {
    const { data: adminUser } = useQuery({ ...adminUserOptions(), enabled: false });
    const { scheduleAction, cancelAction, pauseActivityTracking, resumeActivityTracking } =
        useInactivityTimer();
    const { signOut } = useSignOut();
    const [deadlineAt, setDeadlineAt] = useState<number | null>(null);

    const signOutRef = useRef(signOut);
    const scheduleActionsRef = useRef<() => void>(() => {});

    useEffect(() => {
        signOutRef.current = signOut;
    });

    const adminUserEmail = adminUser?.email;

    useEffect(() => {
        if (adminUserEmail === undefined) return;

        const scheduleActions = () => {
            scheduleAction({
                id: INACTIVITY_WARNING_ACTION_ID,
                delay: INACTIVITY_WARNING_TIMEOUT,
                action: (elapsed) => {
                    if (elapsed < INACTIVITY_SIGN_OUT_TIMEOUT) {
                        pauseActivityTracking();
                        setDeadlineAt(
                            Date.now() + INACTIVITY_SIGN_OUT_TIMEOUT - INACTIVITY_WARNING_TIMEOUT,
                        );
                    }
                },
            });
            scheduleAction({
                id: INACTIVITY_SIGN_OUT_ACTION_ID,
                delay: INACTIVITY_SIGN_OUT_TIMEOUT,
                action: () => signOutRef.current(),
            });
        };

        scheduleActionsRef.current = scheduleActions;
        scheduleActions();

        return () => {
            cancelAction(INACTIVITY_WARNING_ACTION_ID);
            cancelAction(INACTIVITY_SIGN_OUT_ACTION_ID);
        };
    }, [adminUserEmail, scheduleAction, cancelAction, pauseActivityTracking]);

    return (
        <InactivityWarningDialog
            deadlineAt={deadlineAt}
            onContinue={() => {
                setDeadlineAt(null);
                resumeActivityTracking();
                scheduleActionsRef.current();
            }}
            onSignOut={() => {
                setDeadlineAt(null);
                signOutRef.current();
            }}
        />
    );
}
